<template>
  <div class="home">
    <!-- Hero (no test links) -->
    <section class="hero">
      <div class="hero-inner container">
        <div class="hero-badge">Research-Informed Self-Assessments</div>
        <h1>The Science of <em>Understanding Yourself</em></h1>
        <p class="hero-lead">
          SignalRoom is a global portal for cognitive and personality self-assessment —
          64 research-informed instruments, structured scoring, and educational interpretive reports.
        </p>
        <div class="hero-stats">
          <div v-for="s in heroStats" :key="s.label" class="stat-item">
            <strong>{{ s.value }}</strong>
            <span>{{ s.label }}</span>
          </div>
        </div>
        <div class="hero-actions">
          <button class="btn-ghost" @click="scrollTo('trending')">Explore Trending ↓</button>
          <button class="btn-ghost" @click="scrollTo('science')">Our Methodology</button>
        </div>
      </div>
    </section>

    <section class="trust-bar">
      <div class="container trust-inner">
        <span v-for="t in trustItems" :key="t">{{ t }}</span>
      </div>
    </section>

    <!-- Trending — ONLY test entry on homepage -->
    <section id="trending" class="trending-section" v-if="trending.length">
      <div class="container">
        <h2 class="section-title">Most Popular This Week</h2>
        <p class="section-sub">Start your journey with a trending assessment — browse all 64 tests via the top menu</p>
        <div class="trending-grid">
          <article
            v-for="quiz in trending"
            :key="quiz.id"
            class="trending-card"
            @click="goQuiz(quiz.id)"
          >
            <div class="trending-rank">#{{ quiz.rank }}</div>
            <h4>{{ quiz.name }}</h4>
            <p class="trending-cat">{{ quiz.category }}</p>
            <div class="trending-meta">
              <span>{{ quiz.question_count }} questions</span>
              <span>{{ quiz.duration_mins }} min</span>
              <span>{{ formatViews(quiz.views) }} views</span>
            </div>
            <span class="trending-go">Start Assessment →</span>
          </article>
        </div>
      </div>
    </section>

    <!-- Mission -->
    <section class="mission-section">
      <div class="container mission-grid">
        <div>
          <h2 class="section-title">Our Mission</h2>
          <p class="prose">
            For over a century, psychometric science has mapped the architecture of human ability,
            personality, and potential. SignalRoom turns well-known research concepts into accessible
            educational self-assessments without claiming clinical diagnosis, official certification,
            or endorsement from any third-party institution.
          </p>
          <p class="prose">
            Every assessment on our platform is built from published research concepts, transparent
            scoring logic, and careful interpretive writing — not pop-psychology clickbait.
          </p>
        </div>
        <div class="mission-quote">
          <blockquote>
            "Knowing yourself is the beginning of all wisdom."
          </blockquote>
          <cite>— Aristotle</cite>
        </div>
      </div>
    </section>

    <!-- Eight dimensions (info only) -->
    <section class="dimensions-section">
      <div class="container">
        <h2 class="section-title">Eight Dimensions We Measure</h2>
        <p class="section-sub">Each dimension contains 8 flagship assessments — access them from the navigation menu</p>
        <div class="dim-grid">
          <article v-for="cat in categories" :key="cat.id" class="dim-card" :class="'dim-' + cat.slug">
            <span class="dim-icon">{{ catIcon(cat.slug) }}</span>
            <h3>{{ cat.name }}</h3>
            <p>{{ cat.description }}</p>
            <span class="dim-count">{{ cat.quizzes.length }} instruments</span>
          </article>
        </div>
      </div>
    </section>

    <!-- Science -->
    <section id="science" class="science-section">
      <div class="container">
        <h2 class="section-title">The Science Behind Every Score</h2>
        <div class="pillars-grid">
          <article v-for="p in pillars" :key="p.title" class="pillar-card">
            <div class="pillar-icon">{{ p.icon }}</div>
            <h3>{{ p.title }}</h3>
            <p>{{ p.desc }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- Timeline -->
    <section class="timeline-section">
      <div class="container">
        <h2 class="section-title">A Brief History of Psychometrics</h2>
        <div class="timeline">
          <div v-for="item in timeline" :key="item.year" class="timeline-item">
            <div class="tl-year">{{ item.year }}</div>
            <div class="tl-body">
              <h4>{{ item.title }}</h4>
              <p>{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Institutions -->
    <section class="institutions-section">
      <div class="container">
        <h2 class="section-title">Reference Areas We Study</h2>
        <div class="inst-grid">
          <span v-for="inst in institutions" :key="inst" class="inst-badge">{{ inst }}</span>
        </div>
        <p class="reference-note">
          SignalRoom is independent. Referenced names identify study domains only and do not imply sponsorship, certification, or endorsement.
        </p>
      </div>
    </section>

    <!-- Report preview (no CTA button) -->
    <section class="report-section">
      <div class="container report-grid">
        <div>
          <h2 class="section-title">What Your Report Contains</h2>
          <ul class="check-list">
            <li v-for="f in reportFeatures" :key="f">{{ f }}</li>
          </ul>
        </div>
        <div class="report-mock">
          <div class="mock-label">Sample Output</div>
          <div class="mock-score">72<span>/80</span></div>
          <div class="mock-bars">
            <div v-for="bar in mockBars" :key="bar.label" class="mock-row">
              <span>{{ bar.label }}</span>
              <div class="mock-track"><div class="mock-fill" :style="{ width: bar.pct + '%' }" /></div>
            </div>
          </div>
          <p class="mock-text">Structured multi-dimensional narrative with comparative tier placement...</p>
        </div>
      </div>
    </section>

    <!-- Testimonials -->
    <section class="testimonials-section">
      <div class="container">
        <h2 class="section-title">What Users Say</h2>
        <div class="testimonial-grid">
          <blockquote v-for="t in testimonials" :key="t.author" class="testimonial">
            <p>"{{ t.quote }}"</p>
            <cite>{{ t.author }} · {{ t.role }}</cite>
          </blockquote>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="faq-section">
      <div class="container">
        <h2 class="section-title">Frequently Asked Questions</h2>
        <div class="faq-list">
          <details v-for="item in faq" :key="item.q" class="faq-item">
            <summary>{{ item.q }}</summary>
            <p>{{ item.a }}</p>
          </details>
        </div>
      </div>
    </section>

    <!-- Glossary -->
    <section class="glossary-section">
      <div class="container">
        <h2 class="section-title">Psychometrics Glossary</h2>
        <div class="glossary-grid">
          <article v-for="item in glossary" :key="item.title" class="glossary-card">
            <h3>{{ item.title }}</h3>
            <p>{{ item.content }}</p>
          </article>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
const ICONS = {
  intelligence: '🧠', personality: '🎭', career: '💼', financial: '💰',
  emotional: '❤️', language: '🌐', reasoning: '⚖️', culture: '🏛️'
}

export default {
  name: 'HomePage',
  props: {
    categories: { type: Array, default: () => [] }
  },
  data () {
    return {
      heroStats: [
        { value: '64', label: 'Assessments' },
        { value: '8', label: 'Dimensions' },
        { value: '15–30', label: 'Items Each' },
        { value: '0', label: 'Sign-In Required' }
      ],
      trustItems: [
        '✓ Structured Scoring', '✓ Multi-Factor Profiles', '✓ Research-Informed',
        '✓ 800+ Word Reports', '✓ No Account Required'
      ],
      pillars: [
        { icon: '📐', title: 'Question Design', desc: 'Items are grouped around specific skills and traits so each result reflects a clear educational construct.' },
        { icon: '📊', title: 'Structured Scoring', desc: 'Scores are converted into comparative bands that help users interpret their response pattern.' },
        { icon: '🔬', title: 'Construct Mapping', desc: 'Each assessment is mapped to published psychological, educational, or reasoning concepts.' },
        { icon: '📝', title: 'Interpretive Reports', desc: 'Tier-matched narratives translate raw scores into practical reflection prompts and next steps.' }
      ],
      timeline: [
        { year: '1905', title: 'Binet-Simon Scale', desc: 'Alfred Binet creates the first practical intelligence test for identifying children needing educational support in Paris.' },
        { year: '1937', title: 'Wechsler-Bellevue', desc: 'David Wechsler introduces deviation IQ and separate verbal/performance indices — still the clinical gold standard.' },
        { year: '1949', title: 'MMPI Published', desc: 'Minnesota Multiphasic Personality Inventory revolutionizes objective personality assessment in clinical settings.' },
        { year: '1992', title: 'NEO-PI-R Released', desc: 'Costa & McCrae publish the Five-Factor Model instrument, becoming the most validated personality framework.' },
        { year: 'Today', title: 'SignalRoom Portal', desc: '64 research-informed instruments accessible online with structured scoring and educational reports.' }
      ],
      institutions: [
        'Reasoning Research', 'Academic Aptitude', 'Debate Logic', 'Case Analysis',
        'Stanford-Binet', 'NEO-PI-R', 'Holland RIASEC', 'Watson-Glaser',
        'Financial Literacy', 'Negotiation Theory', 'Systems Thinking', 'Big Five Research'
      ],
      reportFeatures: [
        'Composite score with comparative band',
        'Multi-dimensional radar profile',
        'Item-level response summary',
        'Tier-matched narrative report',
        'Development recommendations & retest guidance'
      ],
      mockBars: [
        { label: 'Reasoning', pct: 88 }, { label: 'Memory', pct: 76 },
        { label: 'Patterns', pct: 92 }, { label: 'Control', pct: 81 }
      ],
      testimonials: [
        { quote: 'The reasoning simulation report was remarkably detailed — far beyond any free online IQ-style test I have tried.', author: 'James R.', role: 'Software Engineer' },
        { quote: 'Used the Big Five assessment before a career coaching session. The comparative bands made the discussion much more concrete.', author: 'Sarah L.', role: 'HR Director' },
        { quote: 'I appreciate that SignalRoom cites actual research frameworks instead of inventing personality types from thin air.', author: 'David K.', role: 'Graduate Student' }
      ],
      faq: [
        { q: 'Are results clinically diagnostic?', a: 'No. Assessments are for self-discovery and education only — not a substitute for licensed psychological evaluation.' },
        { q: 'How do I access all 64 tests?', a: 'Use the eight category menus in the top navigation bar. Each expands to show its full suite of 8 assessments.' },
        { q: 'How long does each test take?', a: 'Quick tests: 15 items (~8 min). Standard: 20 items (~15 min). Deep: 25–30 items (~30 min).' },
        { q: 'Is my data stored?', a: 'Responses are processed to generate your report and are not retained long-term. No account is required.' }
      ],
      glossary: [
        { title: 'Flynn Effect', content: 'The sustained worldwide rise in IQ scores throughout the 20th century, implying tests must be periodically re-normed to remain valid interpretive tools.' },
        { title: 'Reliability & Validity', content: 'Reliability is consistency of measurement; validity is whether a test measures its intended construct. Both are required for scientific assessment.' },
        { title: 'Barnum Effect', content: 'Accepting vague descriptions as uniquely personal. SignalRoom avoids this with specific score bands and tier-matched reports.' },
        { title: 'g-Factor', content: 'General intelligence factor underlying performance across diverse cognitive tasks, first identified by Spearman in 1904 and still central to psychometric theory.' }
      ]
    }
  },
  computed: {
    trending () {
      const all = []
      this.categories.forEach(cat => {
        (cat.quizzes || []).forEach(q => all.push({ ...q, category: cat.name }))
      })
      return all.sort((a, b) => b.views - a.views).slice(0, 8).map((q, i) => ({ ...q, rank: i + 1 }))
    }
  },
  methods: {
    catIcon (slug) { return ICONS[slug] || '📋' },
    formatViews (n) { return n >= 1000 ? (n / 1000).toFixed(1) + 'k' : n.toLocaleString() },
    scrollTo (id) {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    },
    goQuiz (id) {
      this.$router.push({ name: 'QuizLanding', params: { id: String(id) } })
    }
  }
}
</script>

<style scoped>
.container { max-width: 1200px; margin: 0 auto; padding: 0 1.25rem; }

.hero {
  background: linear-gradient(160deg, #060e1a, #0d1f3c 50%, #1a3a6b);
  color: #fff;
  padding: 4rem 0 3rem;
}

.hero-badge {
  display: inline-block;
  font-size: 0.68rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #e8c547;
  border: 1px solid rgba(232,197,71,0.35);
  padding: 0.3rem 0.85rem;
  border-radius: 20px;
  margin-bottom: 1.25rem;
}

.hero h1 {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.2rem, 5vw, 3.5rem);
  line-height: 1.1;
  max-width: 720px;
  margin-bottom: 1rem;
}

.hero h1 em { font-style: italic; color: #e8c547; }

.hero-lead {
  font-size: 1.05rem;
  color: rgba(255,255,255,0.72);
  max-width: 640px;
  line-height: 1.75;
  margin-bottom: 2rem;
}

.hero-stats {
  display: flex;
  gap: 2.5rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
}

.stat-item strong {
  display: block;
  font-family: 'Playfair Display', serif;
  font-size: 1.75rem;
  color: #e8c547;
}

.stat-item span {
  font-size: 0.72rem;
  color: rgba(255,255,255,0.45);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.hero-actions { display: flex; gap: 0.75rem; flex-wrap: wrap; }

.btn-ghost {
  background: transparent;
  border: 1.5px solid rgba(255,255,255,0.35);
  color: #fff;
  padding: 0.7rem 1.4rem;
  border-radius: 30px;
  cursor: pointer;
  font-size: 0.88rem;
  transition: all 0.2s;
}

.btn-ghost:hover { border-color: #e8c547; color: #e8c547; }

.trust-bar { background: #e8c547; padding: 0.75rem 0; }
.trust-inner {
  display: flex; flex-wrap: wrap; justify-content: center;
  gap: 1.25rem 2rem; font-size: 0.78rem; font-weight: 600; color: #0d1f3c;
}

.section-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.75rem;
  color: #0d1f3c;
  margin-bottom: 0.35rem;
}

.section-sub { color: #6b7fa0; font-size: 0.9rem; margin-bottom: 1.75rem; }

/* Trending */
.trending-section {
  padding: 3.5rem 0;
  background: #fff;
  scroll-margin-top: 70px;
}

.trending-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}

.trending-card {
  background: linear-gradient(145deg, #0d1f3c, #1a3a6b);
  color: #fff;
  border-radius: 14px;
  padding: 1.35rem;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
}

.trending-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(13,31,60,0.25);
}

.trending-rank {
  font-family: 'Playfair Display', serif;
  font-size: 1.75rem;
  color: #e8c547;
  margin-bottom: 0.5rem;
}

.trending-card h4 { font-size: 0.88rem; line-height: 1.35; margin-bottom: 0.35rem; }
.trending-cat { font-size: 0.72rem; color: rgba(255,255,255,0.5); margin-bottom: 0.75rem; }

.trending-meta {
  display: flex; flex-wrap: wrap; gap: 0.6rem;
  font-size: 0.68rem; color: rgba(255,255,255,0.45);
  margin-bottom: 0.85rem;
}

.trending-go {
  font-size: 0.78rem;
  color: #e8c547;
  font-weight: 700;
}

/* Mission */
.mission-section { padding: 3.5rem 0; background: #f4f6fa; }

.mission-grid {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 2.5rem;
  align-items: center;
}

.prose {
  font-family: 'Source Serif 4', serif;
  color: #4a6080;
  line-height: 1.85;
  margin-bottom: 1rem;
  font-size: 0.95rem;
}

.mission-quote {
  background: #0d1f3c;
  color: #fff;
  border-radius: 16px;
  padding: 2rem;
  border-left: 4px solid #e8c547;
}

.mission-quote blockquote {
  font-family: 'Playfair Display', serif;
  font-size: 1.25rem;
  font-style: italic;
  line-height: 1.5;
  margin-bottom: 0.75rem;
}

.mission-quote cite { font-size: 0.82rem; color: rgba(255,255,255,0.5); }

/* Dimensions info-only */
.dimensions-section { padding: 3.5rem 0; background: #fff; }

.dim-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}

.dim-card {
  border: 1px solid #e8edf5;
  border-radius: 14px;
  padding: 1.35rem;
  background: #fafbfd;
  border-top: 4px solid #1a3a6b;
}

.dim-intelligence { border-top-color: #5c6bc0; }
.dim-personality { border-top-color: #ab47bc; }
.dim-career { border-top-color: #26a69a; }
.dim-financial { border-top-color: #ffa726; }
.dim-emotional { border-top-color: #ef5350; }
.dim-language { border-top-color: #42a5f5; }
.dim-reasoning { border-top-color: #78909c; }
.dim-culture { border-top-color: #8d6e63; }

.dim-icon { font-size: 1.75rem; margin-bottom: 0.65rem; display: block; }

.dim-card h3 {
  font-family: 'Playfair Display', serif;
  font-size: 1rem;
  color: #0d1f3c;
  margin-bottom: 0.4rem;
}

.dim-card p { font-size: 0.82rem; color: #6b7fa0; line-height: 1.55; margin-bottom: 0.75rem; }
.dim-count { font-size: 0.72rem; color: #9aaac0; font-weight: 600; }

/* Science */
.science-section { padding: 3.5rem 0; background: #f4f6fa; scroll-margin-top: 70px; }

.pillars-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.25rem;
}

.pillar-card {
  background: #fff;
  border-radius: 14px;
  padding: 1.5rem;
  border: 1px solid #e8edf5;
}

.pillar-icon { font-size: 1.75rem; margin-bottom: 0.65rem; }
.pillar-card h3 { font-size: 0.95rem; color: #0d1f3c; margin-bottom: 0.5rem; }
.pillar-card p { font-size: 0.84rem; color: #6b7fa0; line-height: 1.6; }

/* Timeline */
.timeline-section { padding: 3.5rem 0; background: #fff; }

.timeline { max-width: 720px; }

.timeline-item {
  display: flex;
  gap: 1.5rem;
  padding-bottom: 1.75rem;
  border-left: 2px solid #e8edf5;
  margin-left: 2rem;
  padding-left: 1.5rem;
  position: relative;
}

.timeline-item::before {
  content: '';
  width: 12px; height: 12px;
  background: #e8c547;
  border-radius: 50%;
  position: absolute;
  left: -7px; top: 4px;
}

.tl-year {
  font-family: 'Playfair Display', serif;
  font-weight: 700;
  color: #1a3a6b;
  min-width: 48px;
  flex-shrink: 0;
}

.tl-body h4 { font-size: 0.92rem; color: #0d1f3c; margin-bottom: 0.3rem; }
.tl-body p { font-size: 0.84rem; color: #6b7fa0; line-height: 1.6; }

/* Institutions */
.institutions-section {
  padding: 3rem 0;
  background: #0d1f3c;
  text-align: center;
}

.institutions-section .section-title { color: #fff; }

.inst-grid {
  display: flex; flex-wrap: wrap; justify-content: center; gap: 0.65rem;
}

.inst-badge {
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.12);
  padding: 0.5rem 1rem;
  border-radius: 30px;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.8);
}

.reference-note {
  max-width: 760px;
  margin: 1.25rem auto 0;
  color: rgba(255,255,255,0.58);
  font-size: 0.78rem;
  line-height: 1.65;
}

/* Report */
.report-section { padding: 3.5rem 0; background: #f4f6fa; }

.report-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2.5rem;
  align-items: center;
}

.check-list { list-style: none; }

.check-list li {
  padding: 0.45rem 0 0.45rem 1.4rem;
  position: relative;
  font-size: 0.9rem;
  color: #4a6080;
}

.check-list li::before {
  content: '✓'; position: absolute; left: 0;
  color: #c9a227; font-weight: 700;
}

.report-mock {
  background: linear-gradient(145deg, #0d1f3c, #1a3a6b);
  border-radius: 18px;
  padding: 2rem;
  color: #fff;
}

.mock-label { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.1em; color: #e8c547; margin-bottom: 0.5rem; }
.mock-score { font-family: 'Playfair Display', serif; font-size: 2.75rem; color: #e8c547; line-height: 1; margin-bottom: 1.25rem; }
.mock-score span { font-size: 1rem; color: rgba(255,255,255,0.4); }

.mock-row {
  display: flex; align-items: center; gap: 0.65rem;
  margin-bottom: 0.55rem; font-size: 0.72rem; color: rgba(255,255,255,0.6);
}

.mock-row span:first-child { width: 80px; }
.mock-track { flex: 1; height: 5px; background: rgba(255,255,255,0.1); border-radius: 3px; overflow: hidden; }
.mock-fill { height: 100%; background: #e8c547; border-radius: 3px; }
.mock-text { margin-top: 1rem; font-size: 0.75rem; color: rgba(255,255,255,0.45); line-height: 1.6; }

/* Testimonials */
.testimonials-section { padding: 3.5rem 0; background: #fff; }

.testimonial-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}

.testimonial {
  background: #f8fafd;
  border-radius: 14px;
  padding: 1.5rem;
  border-left: 4px solid #e8c547;
}

.testimonial p {
  font-family: 'Source Serif 4', serif;
  font-size: 0.9rem;
  color: #4a6080;
  line-height: 1.7;
  margin-bottom: 0.75rem;
  font-style: italic;
}

.testimonial cite { font-size: 0.78rem; color: #9aaac0; }

/* FAQ */
.faq-section { padding: 3rem 0; background: #f4f6fa; }
.faq-list { max-width: 760px; }
.faq-item {
  background: #fff; border-radius: 10px; margin-bottom: 0.6rem;
  border: 1px solid #e8edf5; overflow: hidden;
}
.faq-item summary {
  padding: 1rem 1.25rem; cursor: pointer; font-weight: 600;
  font-size: 0.9rem; color: #0d1f3c; list-style: none;
}
.faq-item summary::-webkit-details-marker { display: none; }
.faq-item p { padding: 0 1.25rem 1rem; font-size: 0.85rem; color: #6b7fa0; line-height: 1.65; }

/* Glossary */
.glossary-section { padding: 3rem 0 4rem; background: #fff; }

.glossary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}

.glossary-card {
  background: #f8fafd;
  border-left: 4px solid #1a3a6b;
  padding: 1.35rem;
  border-radius: 0 12px 12px 0;
}

.glossary-card h3 {
  font-family: 'Playfair Display', serif;
  font-size: 1rem;
  color: #0d1f3c;
  margin-bottom: 0.6rem;
}

.glossary-card p {
  font-family: 'Source Serif 4', serif;
  font-size: 0.88rem;
  color: #4a6080;
  line-height: 1.8;
}

@media (max-width: 768px) {
  .mission-grid, .report-grid { grid-template-columns: 1fr; }
}
</style>
